import React, { useState, useEffect, useContext, useMemo } from 'react';

import styled from 'styled-components';

import {
    EventsSearchProvider,
    useEventsSearchContext,
    useEventsSearchContextDispatch,
} from '@contexts/EventsSearchContext';
import { UserContext } from '@contexts/UserContext';

import { getContactName } from '@hooks/API/Contact/UseContactsList';

import EmptyState from '@components/Gui/EmptyState';
import { Loading } from '@components/Gui/Loading';
import NwDrawer from '@components/Gui/NwDrawer';

import EventsSearchList from '@containers/Booking/SearchEvents/EventsSearchList';

import { getEventsForSearch, groupEventsByProjects, buildEventFiltersFromSimpleParams } from './EventsSearchUtils';
import GroupedEventsSearchList from './GroupedEventsSearchList';

const Wrapper = styled.div`
    .search-content {
    }
`;

const EventsSearchDrawerContent = ({ onClose, title, computedTitle, modelID, shouldOpenFirstCollapse }) => {
    const [filteredEvents, setFilteredEvents] = useState(null);
    const [isFetchingEvents, setFetchingEvents] = useState(true);

    const { currentUser } = useContext(UserContext);
    const { eventFilters, groupByProject } = useEventsSearchContext();
    const dispatch = useEventsSearchContextDispatch();

    // Open first collapse when modelID is present and shouldOpenFirstCollapse is true
    useEffect(() => {
        if (shouldOpenFirstCollapse && modelID && filteredEvents && !isFetchingEvents) {
            // Get all project IDs - try different structures based on groupByProject state
            let projectIds = [];

            if (filteredEvents.Events && filteredEvents.Events.length > 0) {
                // Grouped mode: Events array with Projects
                projectIds = filteredEvents.Events.filter((event) => event?.Project?.ID).map((event) =>
                    String(event.Project.ID)
                );
            } else if (Array.isArray(filteredEvents) && filteredEvents.length > 0) {
                // Ungrouped mode: flat array - get unique project IDs
                const uniqueIds = new Set();
                filteredEvents.forEach((event) => {
                    if (event?.Project?.ID) {
                        uniqueIds.add(String(event.Project.ID));
                    }
                });
                projectIds = Array.from(uniqueIds);
            }

            if (projectIds.length > 0) {
                // Timeout to ensure the component is fully rendered
                const timer = setTimeout(() => {
                    // Open panel '1' for ALL projects
                    projectIds.forEach((projectId) => {
                        dispatch({
                            type: 'SetActiveTransactionCollapse',
                            projectId: projectId,
                            collapseKey: '1',
                        });
                    });
                }, 300);

                return () => clearTimeout(timer);
            }
        }
    }, [shouldOpenFirstCollapse, modelID, filteredEvents, isFetchingEvents, dispatch]);

    const fetchEvents = async () => {
        setFetchingEvents(true);
        try {
            const result = await getEventsForSearch(eventFilters, currentUser, groupByProject);
            setFilteredEvents(result);
        } catch (error) {
            console.error('Error fetching events:', error);
            setFilteredEvents([]);
        } finally {
            setFetchingEvents(false);
        }
    };

    useEffect(() => {
        if (eventFilters) {
            fetchEvents();
        }
    }, [eventFilters, currentUser]);

    const groupEvents = async () => {
        if (filteredEvents) {
            if (groupByProject && !filteredEvents.Projects) {
                // Currently ungrouped, need to group
                const groupedEvents = await groupEventsByProjects(filteredEvents);
                setFilteredEvents(groupedEvents);
            } else if (!groupByProject && filteredEvents.Projects) {
                // Currently grouped, need to ungroup
                setFilteredEvents([...filteredEvents.Events]);
            }
        }
    };

    useEffect(() => {
        if (filteredEvents) {
            groupEvents();
        }
    }, [groupByProject]);

    return (
        <NwDrawer title={computedTitle || title} open onClose={onClose} width="80vw" maxWidth="1200px">
            <Wrapper>
                <div className="search-content">
                    {isFetchingEvents ? (
                        <Loading textBlack paddingTop="10rem" />
                    ) : groupByProject ? (
                        <GroupedEventsSearchList
                            events={filteredEvents || { Events: [], Projects: [] }}
                            loading={isFetchingEvents}
                            onFetchEvents={fetchEvents}
                            modelID={modelID}
                        />
                    ) : (
                        <EventsSearchList
                            events={filteredEvents || []}
                            loading={isFetchingEvents}
                            onFetchEvents={fetchEvents}
                            modelID={modelID}
                        />
                    )}
                </div>
            </Wrapper>
        </NwDrawer>
    );
};

const EventsSearchDrawer = ({
    // Complex interface
    eventFilters,

    // Simple interface
    modelId,
    clientId,
    modelName,
    clientName,
    eventTypes,

    // Storage and defaults
    storageKey,
    defaultGroupByProject = true,
    defaultShowTransactions = true,
    defaultSort = 'time-down',
    openFirstCollapse = false,

    // Common
    onClose,
    title = 'Events Search',
    visible = true,
}) => {
    // Determine which mode we're in
    const isSimpleMode = (modelId || clientId) && eventTypes;

    // Build filters for simple mode
    const computedEventFilters = useMemo(() => {
        if (isSimpleMode) {
            return buildEventFiltersFromSimpleParams(modelId, clientId, eventTypes);
        }
        return eventFilters;
    }, [modelId, clientId, eventTypes, eventFilters, isSimpleMode]);

    // Build dynamic title when using simple mode
    const computedTitle = useMemo(() => {
        if (title && title !== 'Events Search') return title;

        if (isSimpleMode && (modelName || clientName)) {
            const primary = clientName || getContactName(clientId);
            const secondary = modelName || getContactName(modelId);
            return `${primary} + ${secondary} Projects`;
        }

        return title;
    }, [title, isSimpleMode, modelName, clientName, modelId, clientId]);

    if (!visible) return null;

    return (
        <EventsSearchProvider
            storageKey={storageKey}
            initialSort={defaultSort}
            initialGroupByProject={defaultGroupByProject}
            initialShowTransactions={defaultShowTransactions}
            initialEventFilters={computedEventFilters}
            initialActiveTransactionCollapse={{}}
            modelId={modelId}
            clientId={clientId}
            isDrawerMode={true}
        >
            <EventsSearchDrawerContent
                onClose={onClose}
                title={title}
                computedTitle={computedTitle}
                modelID={isSimpleMode ? modelId : computedEventFilters && computedEventFilters.modelID}
                shouldOpenFirstCollapse={openFirstCollapse}
            />
        </EventsSearchProvider>
    );
};

export default EventsSearchDrawer;
